import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import userMetadataApi from '../api/userMetadataApi';
import { UserMetadataResponse } from '../api/types';
import { useWorkspace } from './WorkspaceContext';
import { bootstrapSessionFromLocal, clearPersistedSession, getStoredToken } from '../utils/authStorage';
import { clearAllChatSessionStorageKeys } from '../utils/clearChatSessionStorage';

interface UserContextType {
    userMetadata: UserMetadataResponse | null;
    isLoading: boolean;
    isAdmin: boolean;
    refetch: () => void;
    logout: () => void;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

export const UserProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    bootstrapSessionFromLocal();
    const queryClient = useQueryClient();
    const { activeBusinessId } = useWorkspace();
    const [isAdmin, setIsAdmin] = useState<boolean>(() => {
        return sessionStorage.getItem('user_role') == 'admin';
    });

    const { data: userMetadata, isLoading, refetch } = useQuery({
        queryKey: ['userMetadata', activeBusinessId],
        queryFn: async () => {
            const data = await userMetadataApi.getUserMetadata(activeBusinessId || undefined);
            // Update session storage and state upon successful fetch
            if (data.role) {
                sessionStorage.setItem('user_role', data.role);
                setIsAdmin(data.role == 'admin');
            } else {
                sessionStorage.removeItem('user_role');
                setIsAdmin(false);
            }
            return data;
        },
        enabled: !!getStoredToken(),
        retry: false,
    });

    // Ensure state sync if sessionStorage changes (unlikely in SPA but good practice)
    useEffect(() => {
        if (userMetadata?.role) {
            setIsAdmin(userMetadata.role == 'admin');
        }
    }, [userMetadata]);

    const logout = () => {
        clearPersistedSession();
        clearAllChatSessionStorageKeys();
        sessionStorage.removeItem("chat_messages");
        sessionStorage.removeItem("conversation_id");
        // Explicitly clear role
        sessionStorage.removeItem("user_role");
        setIsAdmin(false);
        queryClient.clear();
        window.location.replace("/");
    };

    return (
        <UserContext.Provider value={{ userMetadata: userMetadata || null, isLoading, isAdmin, refetch, logout }}>
            {children}
        </UserContext.Provider>
    );
};

export const useUser = () => {
    const context = useContext(UserContext);
    if (context === undefined) {
        throw new Error('useUser must be used within a UserProvider');
    }
    return context;
};
